import React, { useCallback, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useDRBFMCaseWorkflow } from '../../../hooks/useDRBFMCaseWorkflow';
import { drbfmService } from '../../../services/drbfm.service';
import DRBFMResultsStep from '../steps/DRBFMResultsStep';
import DRBFMCaseIntakeStep from './DRBFMCaseIntakeStep';
import DRBFMChangeReviewStep from './DRBFMChangeReviewStep';
import DRBFMPrecheckStep from './DRBFMPrecheckStep';

interface DRBFMCaseWorkflowProps {
  cadEnabled?: boolean | null;
}

const DRBFMCaseWorkflow: React.FC<DRBFMCaseWorkflowProps> = ({
  cadEnabled = null,
}) => {
  const { t } = useTranslation();
  const workflow = useDRBFMCaseWorkflow();
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const handleExport = useCallback(async () => {
    const session = workflow.results?.session;
    if (!session) {
      setExportError(t('drbfm.case.exportMissing', 'エクスポート対象のDRBFM結果がありません'));
      return;
    }

    setIsExporting(true);
    setExportError(null);
    try {
      const filename = `drbfm_case_${session.itemName}_${session.id.slice(0, 8)}.html`;
      await drbfmService.downloadExportedHTML(session.id, filename);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : t('drbfm.case.exportFailed', 'HTMLエクスポートに失敗しました'));
    } finally {
      setIsExporting(false);
    }
  }, [workflow.results?.session, t]);

  const renderStep = () => {
    switch (workflow.caseStep) {
      case 'case-intake':
        return (
          <DRBFMCaseIntakeStep
            isLoading={workflow.isLoading}
            onSubmit={workflow.createAndExtract}
          />
        );

      case 'change-review':
        return (
          <DRBFMChangeReviewStep
            changes={workflow.designChanges}
            isLoading={workflow.isLoading}
            onChange={workflow.setDesignChanges}
            onSubmit={workflow.saveChangesAndGeneratePrecheck}
            onBack={workflow.reset}
          />
        );

      case 'precheck':
        return (
          <DRBFMPrecheckStep
            items={workflow.precheckItems}
            isLoading={workflow.isLoading}
            onChange={workflow.setPrecheckItems}
            onSubmit={workflow.saveAnswersAndAnalyze}
            onBack={() => workflow.setCaseStep('change-review')}
          />
        );

      case 'analysis':
        return (
          <Box
            sx={{
              minHeight: 280,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Stack spacing={2} alignItems="center">
              <CircularProgress aria-label={t('drbfm.case.analyzingLabel', 'DRBFMケース分析中')} />
              <Typography color="text.secondary">
                {t('drbfm.case.analyzingLabel', 'DRBFMケース分析中')}
              </Typography>
            </Stack>
          </Box>
        );

      case 'results':
        return (
          <Stack spacing={2}>
            {!workflow.results && workflow.latestAnalysisSessionId && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={
                    workflow.isLoading
                      ? <CircularProgress size={16} color="inherit" />
                      : <RefreshIcon />
                  }
                  disabled={workflow.isLoading}
                  onClick={workflow.retryFetchResults}
                >
                  {workflow.isLoading
                    ? t('drbfm.case.refetching', '再取得中')
                    : t('drbfm.case.refetchResults', '結果を再取得')}
                </Button>
              </Box>
            )}
            <DRBFMResultsStep
              results={workflow.results}
              onExport={handleExport}
              onNewAnalysis={workflow.reset}
              isExporting={isExporting}
              exportError={exportError}
              drbfmSessionId={workflow.results?.session?.id}
              drbfmItemName={workflow.results?.session?.itemName}
              cadEnabled={cadEnabled}
            />
          </Stack>
        );

      default:
        return null;
    }
  };

  return (
    <Stack spacing={2.5}>
      {workflow.error && (
        <Alert severity="error">
          {workflow.error}
        </Alert>
      )}
      {renderStep()}
    </Stack>
  );
};

export default DRBFMCaseWorkflow;
